import type { ComponentProps } from 'react';
import type React from 'react';

import { Helmet } from 'react-helmet-async';

import { parseHtml } from '@change-corgi/core/react/html';

type Props = {
	html: string;
	aspectRatio?: number | string;
};

const EmbeddedMediaInner: React.FC<Props & ComponentProps<'div'>> = ({ html, aspectRatio, ...rest }) => {
	return (
		<div
			style={
				{
					'--embedded-media-aspect-ratio': aspectRatio,
				} as React.CSSProperties
			}
			className="[&_iframe]:aspect-(--embedded-media-aspect-ratio) [&_iframe]:h-auto"
			suppressHydrationWarning
			{...rest}
		>
			{parseHtml(html, {
				allowedTags: ['iframe'],
				allowedAttrs: [
					'scrolling',
					'frameborder',
					'allowfullscreen',
					'allow',
					// https://iframely.com/docs/lazy-load#what-you-get
					'data-iframely-url',
					'data-img',
				],
				suppressHydrationWarning: true,
			})}
		</div>
	);
};

const SCRIPT_ID = 'iframely-embed-script';
// This prevents from running the full vendor JS every time we display an embedded media
const SCRIPT = `
(function(){
	if (document.getElementById('${SCRIPT_ID}')) return;
	var p = document.getElementsByTagName('script')[0];
	var s = document.createElement('script');
	s.id = '${SCRIPT_ID}';
	s.async = 1;
	s.charSet = 'utf-8';
	s.src = '//cdn.iframe.ly/embed.js';
	p.parentNode.insertBefore(s, p);
}());
`;

export const EmbeddedMedia: React.FC<Props & ComponentProps<'div'>> = (props) => {
	const { html } = props;
	const accessibleHtml = html.replace(/<iframe\b/gi, '<iframe aria-label="Media"');

	return (
		<>
			<Helmet>
				<script type="text/javascript" id="iframely-embed-script-injector">
					{SCRIPT}
				</script>
			</Helmet>
			<EmbeddedMediaInner {...props} html={accessibleHtml} />
		</>
	);
};
